// ======================================================================
// 📁 File: src/modules/ipe/IPELayout.jsx
// 📝 Purpose: IPE Module Layout (Sidebar + Content)
//        • Mirrors PPELayout behavior exactly
//        • Uses IPE-specific sidebar + localStorage key
// ======================================================================

import React, { useEffect, useState } from "react";
import IPESidebar from "./layout/IPESidebar";

/**
 * Helper: Safe fetch of CSS variable
 */
function getCssVar(varName, fallback = null) {
  try {
    const val = getComputedStyle(document.documentElement)
      .getPropertyValue(varName)
      .trim();
    return val || fallback;
  } catch {
    return fallback;
  }
}

export default function IPELayout({ children }) {

  // -------------------------------------------------------------------
  // 1. Hide page header
  // -------------------------------------------------------------------
  useEffect(() => {
    const selectors = [
      ".page-header",
      ".content-header",
      ".module-header",
      ".top-header",
      ".header-wrapper",
      ".page-title-wrapper",
    ];

    let found = null;
    for (const s of selectors) {
      const el = document.querySelector(s);
      if (el) {
        found = el;
        break;
      }
    }

    if (found) {
      found.dataset.originalDisplay = found.style.display || "";
      found.style.display = "none";
    }

    return () => {
      if (found) {
        found.style.display = found.dataset.originalDisplay;
      }
    };
  }, []);

  // -------------------------------------------------------------------
  // 2. Sidebar collapse control
  // -------------------------------------------------------------------
  const collapsedLS = localStorage.getItem("ipe_sidebar_collapsed");
  const initialExpanded = collapsedLS === null ? true : collapsedLS === "false";

  const [sidebarExpanded, setSidebarExpanded] = useState(initialExpanded);

  useEffect(() => {
    const handler = () => {
      const collapsed =
        localStorage.getItem("ipe_sidebar_collapsed") === "true";
      setSidebarExpanded(!collapsed);
    };
    window.addEventListener("ipe_sidebar_update", handler);
    return () =>
      window.removeEventListener("ipe_sidebar_update", handler);
  }, []);

  const handleSidebarToggle = (isExpanded) => {
    setSidebarExpanded(Boolean(isExpanded));
    localStorage.setItem(
      "ipe_sidebar_collapsed",
      isExpanded ? "false" : "true"
    );
    window.dispatchEvent(new Event("ipe_sidebar_update"));
  };

  // -------------------------------------------------------------------
  // 3. Main content area
  // -------------------------------------------------------------------
  const expandedWidth = getCssVar("--tf-sidebar-w", "260px");
  const collapsedWidth = getCssVar("--tf-sidebar-w-collapsed", "64px");

  const contentStyle = {
    transition: "margin-left 0.22s ease, width 0.22s ease",
    marginLeft: sidebarExpanded ? expandedWidth : collapsedWidth,
    padding: "18px",
    minHeight: "100vh",
    background: "#faf7fb",
    boxSizing: "border-box",
  };

  return (
    <div className="ipe-layout-wrapper" style={{ minHeight: "100vh" }}>
      <IPESidebar onToggle={handleSidebarToggle} />

      <main className="ipe-layout-content" style={contentStyle}>
        {children}
      </main>
    </div>
  );
}